

import { FormInput } from "@/components/Inputs/FormInput";
import { PrimaryButton } from "@/components/Buttons/PrimaryButton";
import { SecondaryButton } from "@/components/Buttons/SecondaryButton";
import { Spacing } from "@/components/Spacing/Spacing";
import { TertiaryButton } from "@/components/Buttons/TertiaryButton";
import { Link } from "react-router-dom";
import { useTranslations } from "next-intl";
import { useLoginForm } from "./useLoginForm";
import { IconCircleCheck } from "@tabler/icons-react";

/**
 * LoginForm component - Handles the login form UI and interactions
 * Uses the useLoginForm hook for state management and form handling
 * @returns Login form UI with email, password inputs and action buttons
 */
export default function LoginForm({
  showResetSuccess = false,
}: {
  showResetSuccess?: boolean;
}) {
  // Get translations for the login form UI
  const t = useTranslations("login");
  const tStatus = useTranslations("optionsMultiSingleSelect");

  // Get form state and handlers from the custom hook
  const {
    formData,
    errors,
    isLoading,
    handleInputChange,
    handleSubmit,
    prefetchDashboard,
    rateLimited,
  } = useLoginForm();

  // Submit form on enter key press
  const submitOnEnter = () => {
    if (!isLoading && !rateLimited) {
      handleSubmit();
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="w-full">
      {/* L7-REVAL — `noValidate` so the app's OWN validation is what runs.
          Commit 4366f35a switched this email field text→email. The browser's
          native HTML5 email constraint then blocked the form submit BEFORE
          onSubmit→handleSubmit, so validateEmail never ran and the styled,
          translated "Invalid email address" never rendered — the user saw only
          the browser's native bubble, in the BROWSER's locale, unstyled, and
          positioned by the browser.
          Kept as type="email" rather than reverted to text: the type is what
          gives mobile users the @/. keyboard and the right autofill hint. It
          is the native VALIDATION that had to go, not the input type. Every
          form here renders its own `error=` for the field, so nothing is left
          unvalidated. */}
      {showResetSuccess && (
        <div className="surface-success rounded-xl outline-success border p-3 mb-4 flex items-center justify-center gap-2">
          <IconCircleCheck size={18} className="font-success" />
          <p className="body-2-regular font-success">{tStatus("SUCCESS")}</p>
        </div>
      )}

      {/* Email input field */}
      <FormInput
        autoComplete={true}
        id="email"
        type="email"
        inputProps={{ autoCapitalize: "none", autoCorrect: "off", spellCheck: false }}
        value={formData.email}
        label={t("email")}
        error={errors.email}
        onChangeAction={(value) => handleInputChange("email", value)}
        onEnterAction={submitOnEnter}
      />

      {/* Vertical spacing between form elements */}
      <Spacing height="h-6" />

      {/* Password input field */}
      <FormInput
        id="password"
        type="password"
        value={formData.password}
        label={t("password")}
        error={errors.password}
        onChangeAction={(value) => handleInputChange("password", value)}
        onEnterAction={submitOnEnter}
      />

      {/* Vertical spacing before buttons */}
      <Spacing height="h-6" />

      {/* Login button - shows loading state during submission */}
      <PrimaryButton
        onHover={prefetchDashboard}
        type="submit"
        text={t("login")}
        size="small"
        disabled={isLoading || rateLimited}
        loading={isLoading}
      />

      {/* Vertical spacing between buttons */}
      <Spacing height="h-3" />

      {/* Register account link - used Link because of prefetching */}
      <Link to="/auth/register">
        <SecondaryButton text={t("noAccount")} size="small" />
      </Link>

      {/* Vertical spacing between buttons */}
      <Spacing height="h-3" />

      {/* Forgot password link - used Link because of prefetching */}
      <div className="flex justify-center">
        <Link to="/auth/reset-password">
          <TertiaryButton text={t("forgotPassword")} size="small" width="w-auto" />
        </Link>
      </div>
    </form>
  );
}
